import React, { FC, useEffect, useState } from 'react';
import { ImageRenderer } from 'src/common/ImageRenderer';
import JourneySlideover from 'src/common/JourneySlideOver';
import { VisitorView } from 'src/editor/insights/constants';
import { TooltipComponent } from 'src/common/tooltip/component';
import { getDeviceTypeIcon, getDeviceTypeTooltip } from 'src/editor/insights/visitor-card';
import { Journey } from 'src/common/interfaces/journey.interface';
import { apiGetVisitorJourneys } from 'src/utils/journeyApi';
import { useCurrentOrganization } from 'src/store/organization';
import LoadingSpinner from 'src/common/components/loading-spinner';
import OrganizationLink from 'src/common/OrganizationLink';
import FacebookSocialIcon from 'src/monet/icons/FacebookSocialIcon';
import LinkedinSocialIcon from 'src/monet/icons/LinkedinSocialIcon';
import TwitterSocialIcon from 'src/monet/icons/TwitterSocialIcon';
import { SocialMediaDomainType } from '../../helpers';

type Props = {
  open: boolean;
  onClose: () => void;
  sessionUuid: string;
  visitorView: VisitorView;
};

function getOccupationCompanyInfo(visitorView: VisitorView): string | undefined {
  if (visitorView.occupation_title && visitorView.company?.name) {
    return `${visitorView.occupation_title}, ${visitorView.company?.name}`;
  }
  if (visitorView.occupation_title) {
    return visitorView.occupation_title;
  }
  if (visitorView.company?.name) {
    return visitorView.company?.name;
  }
  return undefined;
}

function getLocationTimeInfo(visitorView: VisitorView): string | undefined {
  if (visitorView.location) {
    return `${visitorView.location}, ${visitorView.created_at}`;
  }
  return visitorView.created_at;
}

const SOCIAL_ICON_MAPPING: Record<SocialMediaDomainType, () => JSX.Element> = {
  facebook: () => <FacebookSocialIcon />,
  linkedin: () => <LinkedinSocialIcon />,
  twitter: () => <TwitterSocialIcon />,
};

export const DashboardInsightsJourneysJourneyVisitorDetailsSlideover: FC<Props> = ({
  open,
  onClose,
  sessionUuid,
  visitorView,
}) => {
  const occupationCompanyInfo = visitorView && getOccupationCompanyInfo(visitorView);
  const locationTimeInfo = visitorView && getLocationTimeInfo(visitorView);

  const [fetching, setFetching] = useState(true);
  const [visitorJourneys, setVisitorJourneys] = useState<Journey[]>([]);
  const { currentOrganization } = useCurrentOrganization();

  const fetchVisitorJourneys = async () => {
    try {
      setFetching(true);
      const response = await apiGetVisitorJourneys(currentOrganization.id, sessionUuid);
      setVisitorJourneys(response.journeys);
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchVisitorJourneys();
  }, [open, currentOrganization.id, sessionUuid]);

  const renderJourneysViewed = () => {
    if (fetching) {
      return <LoadingSpinner containerClassName='mt-20' />;
    }
    if (visitorJourneys.length === 0) {
      return <div className='text-bedrock-black text-bedrock-p'>No journeys found</div>;
    }
    return (
      <div className='w-full flex flex-col'>
        {visitorJourneys.map((journey) => (
          <OrganizationLink
            key={journey.uuid}
            to={`/insights/journeys/${journey.uuid}`}
            className='transition-opacity text-bedrock-black text-bedrock-p rounded-lg odd:bg-bedrock-light-gray even:bg-bedrock-white p-2 has-hover'
          >
            {journey.name}
          </OrganizationLink>
        ))}
      </div>
    );
  };

  return (
    <JourneySlideover
      isBedrock
      open={open}
      panelClassName='w-[368px]'
      title={visitorView.name || 'Anonymous'}
      onClose={onClose}
    >
      <div className='flex flex-col w-full items-start gap-6'>
        <div className='flex flex-col gap-2'>
          <div className='flex flex-col'>
            {occupationCompanyInfo && <div className='text-bedrock-p text-bedrock-black'>{occupationCompanyInfo}</div>}
            {visitorView.email && (
              <div className='text-bedrock-p text-bedrock-black underline'>
                <a
                  onClick={(e) => e.stopPropagation()}
                  href={`mailto:${visitorView.email}`}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {visitorView.email}
                </a>
              </div>
            )}
            {locationTimeInfo && <div className='mt-1 text-bedrock-p-small text-bedrock-black'>{locationTimeInfo}</div>}
          </div>
        </div>
        <div className='flex items-center'>
          {visitorView.company?.logo && (
            <div>
              {visitorView.company?.domain ? (
                <a
                  href={`https://${visitorView.company?.domain}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={(e) => e && e.stopPropagation()}
                >
                  <ImageRenderer className='rounded-md h-4 mr-6' src={visitorView.company?.logo} />
                </a>
              ) : (
                <ImageRenderer className='rounded-md h-4 mr-6' src={visitorView.company?.logo} />
              )}
            </div>
          )}
          {(visitorView.social_media || []).length > 0 && (
            <div className='flex gap-1 mr-6'>
              {(visitorView.social_media || []).map(({ handle, url, type }) => (
                <a
                  key={`${type}-profile-${handle}`}
                  onClick={(e) => e && e.stopPropagation()}
                  href={url}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {SOCIAL_ICON_MAPPING[type]()}
                </a>
              ))}
            </div>
          )}
          <div>
            <TooltipComponent placement='top' tooltipContent={getDeviceTypeTooltip(visitorView.device_type)}>
              {getDeviceTypeIcon(visitorView.device_type)}
            </TooltipComponent>
          </div>
          {visitorView.ip_address ? (
            <div className='mt-1 ml-2 text-bedrock-p-small text-bedrock-black'>{visitorView.ip_address}</div>
          ) : null}
        </div>
        <div className='w-full h-px bg-bedrock-gray-medium rounded-full'></div>
        <div className='w-full flex flex-col gap-4'>
          <div className='text-bedrock-black heading-bedrock-tertiary'>Journeys viewed</div>
          {renderJourneysViewed()}
        </div>
      </div>
    </JourneySlideover>
  );
};
